/**
 * Pagination 컴포넌트
 *
 * 공통 페이지네이션 UI
 * - 고정된 UI 크기로 일관된 레이아웃 유지
 * - 패턴: [이전] [1] [ellipsis?] [5개 중간] [ellipsis?] [마지막] [다음]
 */

import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const pages = [];

  // 이전 페이지 버튼 (고정 너비)
  pages.push(
    <button
      key="prev"
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
      className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors whitespace-nowrap w-[4rem]"
    >
      이전
    </button>
  );

  // 항상 고정된 7개 슬롯: [1] [ellipsis?] [5개 중간] [ellipsis?] [마지막]
  if (totalPages <= 7) {
    // 전체 페이지가 7개 이하면 모두 표시하고 나머지는 빈 공간
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={`px-2 py-2 rounded-lg text-sm font-medium transition-colors w-[3.5rem] text-center ${
            i === currentPage
              ? "bg-indigo-500 text-white"
              : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}
        >
          {i}
        </button>
      );
    }
    // 나머지 공간을 빈 요소로 채움
    for (let i = totalPages + 1; i <= 7; i++) {
      pages.push(
        <span
          key={`empty-${i}`}
          className="w-[3.5rem] opacity-0 pointer-events-none"
        >
          &nbsp;
        </span>
      );
    }
  } else {
    // 항상 고정 패턴: [1] [ellipsis?] [5개 중간] [ellipsis?] [마지막] = 7개 슬롯
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    const showStartEllipsis = startPage > 2;
    const showEndEllipsis = endPage < totalPages - 1;

    // 첫 페이지 (항상 표시)
    pages.push(
      <button
        key={1}
        onClick={() => onPageChange(1)}
        className={`px-2 py-2 rounded-lg text-sm font-medium transition-colors w-[3.5rem] text-center ${
          1 === currentPage
            ? "bg-indigo-500 text-white"
            : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
        }`}
      >
        1
      </button>
    );

    // 시작 생략 표시 (항상 공간 확보)
    pages.push(
      showStartEllipsis ? (
        <span
          key="ellipsis1"
          className="px-2 text-slate-500 w-[3.5rem] text-center"
        >
          ...
        </span>
      ) : (
        <span
          key="placeholder1"
          className="w-[3.5rem] opacity-0 pointer-events-none"
        >
          &nbsp;
        </span>
      )
    );

    // 중간 페이지들 (5개 슬롯, 실제로는 startPage~endPage)
    const middlePages = [];
    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== totalPages) {
        middlePages.push(i);
      }
    }

    // 5개 슬롯에 맞춰서 표시
    for (let i = 0; i < 5; i++) {
      if (i < middlePages.length) {
        const pageNum = middlePages[i]!;
        pages.push(
          <button
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            className={`px-2 py-2 rounded-lg text-sm font-medium transition-colors w-[3.5rem] text-center ${
              pageNum === currentPage
                ? "bg-indigo-500 text-white"
                : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {pageNum}
          </button>
        );
      } else {
        pages.push(
          <span
            key={`middle-empty-${i}`}
            className="w-[3.5rem] opacity-0 pointer-events-none"
          >
            &nbsp;
          </span>
        );
      }
    }

    // 끝 생략 표시 (항상 공간 확보)
    pages.push(
      showEndEllipsis ? (
        <span
          key="ellipsis2"
          className="px-2 text-slate-500 w-[3.5rem] text-center"
        >
          ...
        </span>
      ) : (
        <span
          key="placeholder2"
          className="w-[3.5rem] opacity-0 pointer-events-none"
        >
          &nbsp;
        </span>
      )
    );

    // 마지막 페이지 (항상 표시)
    pages.push(
      <button
        key={totalPages}
        onClick={() => onPageChange(totalPages)}
        className={`px-2 py-2 rounded-lg text-sm font-medium transition-colors w-[3.5rem] text-center ${
          totalPages === currentPage
            ? "bg-indigo-500 text-white"
            : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
        }`}
      >
        {totalPages}
      </button>
    );
  }

  // 다음 페이지 버튼 (고정 너비)
  pages.push(
    <button
      key="next"
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-colors whitespace-nowrap w-[4rem]"
    >
      다음
    </button>
  );

  return <div className="flex items-center gap-1">{pages}</div>;
};

export default Pagination;

